// Pure helpers for the Alerts page: formatting, filtering and the client-side
// validation that mirrors the server's, so a typo is caught before the round
// trip. Kept out of the component so it can be unit tested.

// ALERTS_CHANGED_EVENT is dispatched on window after a dismiss or undo, so the
// top-bar bell recounts at once instead of at its next poll.
export const ALERTS_CHANGED_EVENT = "hostit:alerts-changed";

// SEVERITIES in ascending order, as the minimum-severity picker offers them.
export const SEVERITIES = [
  { value: "info", label: "Info" },
  { value: "warning", label: "Warning" },
  { value: "critical", label: "Critical" },
];

// PROVIDERS describes each destination kind: the fields it asks for and
// whether it holds a credential (never shown back once stored).
export const PROVIDERS = [
  {
    value: "ntfy",
    label: "ntfy",
    help: "Push notifications to your phone or desktop through ntfy.sh or your own ntfy server.",
    fields: [
      { name: "server", label: "Server", placeholder: "https://ntfy.sh (default)", optional: true },
      { name: "topic", label: "Topic", placeholder: "hostit-alerts" },
    ],
    secret: { label: "Access token", placeholder: "tk_... (optional, for protected topics)", optional: true },
  },
  {
    value: "slack",
    label: "Slack",
    help: "This instance's Slack bot posts to a channel. Invite the bot to the channel first (/invite @bot in Slack).",
    fields: [{ name: "channel", label: "Channel", placeholder: "#ops or C0123456789" }],
    // A person's Slack alerts are a DM from the bot, found by their email
    personalHelp: "This instance's Slack bot sends you a direct message. You are found in Slack by your email; enter another one if your Slack account uses a different address. For a different address the bot first sends a 6-digit code to that Slack account, and alerts start once you enter it here.",
    personalFields: [{ name: "email", label: "Slack email", optional: true }],
    secret: null,
  },
  {
    value: "email",
    label: "Email",
    help: "Plain-text mail through the relay this instance is configured with.",
    fields: [{ name: "to", label: "Address", placeholder: "you@example.com" }],
    secret: null,
  },
];

const NTFY_TOPIC = /^[-_A-Za-z0-9]{1,64}$/;
const SLACK_CHANNEL = /^(#[a-z0-9._-]{1,80}|[A-Z][A-Z0-9]{3,})$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// relativeAge renders "20s ago" style ages; empty for no timestamp.
export const relativeAge = (iso, nowMs = Date.now()) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const seconds = Math.max(0, Math.round((nowMs - t) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86400)}d ago`;
};

// severityInfo gives a severity its label, badge class and icon name; text
// and icon carry the meaning, the colour only reinforces it.
export const severityInfo = (severity) => {
  switch (severity) {
    case "critical":
      return { label: "Critical", className: "alert-sev alert-sev-critical", icon: "critical" };
    case "warning":
      return { label: "Warning", className: "alert-sev alert-sev-warning", icon: "warning" };
    default:
      return { label: "Info", className: "alert-sev alert-sev-info", icon: "info" };
  }
};

// destinationSummary says where a destination delivers, in one short string.
export const destinationSummary = (d) => {
  const cfg = d.config || {};
  switch (d.provider) {
    case "ntfy": {
      const host = (cfg.server || "https://ntfy.sh").replace(/^https?:\/\//, "").replace(/\/$/, "");
      return `${host}/${cfg.topic || ""}`;
    }
    case "slack":
      if (cfg.user) return `Direct message to ${cfg.email || cfg.user}`;
      return (cfg.channel || "").startsWith("#") ? cfg.channel : `Channel ${cfg.channel || ""}`;
    case "email":
      return cfg.to || "";
    default:
      return "";
  }
};

// filterAlerts applies the inbox filters: dismissed hidden unless asked for,
// then scope (the audience: "system" or "user"), severity and type.
export const filterAlerts = (items, { scope = "", severity = "", type = "", showDismissed = false } = {}) =>
  (items || []).filter(
    (a) =>
      (showDismissed || !a.dismissed) &&
      (!scope || a.audience_kind === scope) &&
      (!severity || a.severity === severity) &&
      (!type || a.type === type),
  );

// validateDestination returns the first problem with a destination form, or
// "" when it can be sent. hasSecret says a credential is already stored, so an
// empty secret field means "keep it" rather than "none".
export const validateDestination = ({ provider, label, config = {}, secret = "", hasSecret = false, personal = false }) => {
  if (!(label || "").trim()) return "Give the destination a name.";
  switch (provider) {
    case "ntfy": {
      const server = (config.server || "").trim();
      if (server && !/^https:\/\/[^/?#\s]+\/?$/.test(server) && !/^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/?$/.test(server)) {
        return "The server must be an https:// URL with no path, like https://ntfy.sh.";
      }
      if (!NTFY_TOPIC.test((config.topic || "").trim())) return "A topic is one ntfy topic name: letters, digits, - and _ only.";
      return "";
    }
    case "slack":
      if (personal) {
        const email = (config.email || "").trim();
        if (email && !EMAIL.test(email)) return "Enter the email address you use in Slack, or leave it empty.";
        return "";
      }
      if (!SLACK_CHANNEL.test((config.channel || "").trim())) return "A Slack channel is required: #channel-name or a channel id like C0123456789.";
      return "";
    case "email":
      if (!EMAIL.test((config.to || "").trim())) return "A plain email address is required, like ops@example.com.";
      return "";
    default:
      return "Pick where to send notifications.";
  }
};

// pauseSummary describes a type's pause for the Alert types table: until a
// date, or until someone resumes it.
const formatUntil = (d) => d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });

export const pauseSummary = (t, format = formatUntil) => {
  if (!t || !t.paused) return "";
  if (!t.paused_until) return "Paused until resumed";
  return `Paused until ${format(new Date(t.paused_until))}`;
};

// deliveryStatus words a destination's last notification for the table: was
// it delivered (and when), is the destination failing (and why), or has
// nothing been sent there yet.
export const deliveryStatus = (d, nowMs = Date.now()) => {
  // A Slack DM to another address is not live until its code is confirmed
  if (d.pending_verification) return { label: "Awaiting code", tone: "pending", detail: "" };
  if (d.last_status === "error") return { label: "Failing", tone: "bad", detail: d.last_error || "" };
  if (d.last_status === "ok") {
    const age = relativeAge(d.last_sent_at, nowMs);
    return { label: age ? `Delivered ${age}` : "Delivered", tone: "good", detail: "" };
  }
  return { label: "Not used yet", tone: "muted", detail: "" };
};

// verificationCode returns the 6-digit code the Slack bot sent, spaces
// dropped, or "" when the input is not one.
export const verificationCode = (input) => {
  const code = String(input ?? "").replace(/\s+/g, "");
  return /^\d{6}$/.test(code) ? code : "";
};

// alertPath builds an alert's API path; the id comes from the URL, so it is
// encoded to stay one path segment.
export const alertPath = (id, rest = "") => `/api/alerts/${encodeURIComponent(id)}${rest}`;

// collapseTimeline folds consecutive identical events (same kind and severity)
// into their latest one with a count, so repeated pause-ends read as one row.
export const collapseTimeline = (events) => {
  const rows = [];
  for (const e of events) {
    const prev = rows[rows.length - 1];
    if (prev && prev.kind === e.kind && prev.severity === e.severity) {
      rows[rows.length - 1] = { ...e, count: prev.count + 1 };
    } else {
      rows.push({ ...e, count: 1 });
    }
  }
  return rows;
};

// splitOnLabel finds the resource's name in an alert message, as a whole word,
// so the page can make it a link: [before, name, after], or null.
export const splitOnLabel = (message, label) => {
  if (!message || !label) return null;
  const word = (c) => c !== undefined && /[A-Za-z0-9_-]/.test(c);
  for (let at = message.indexOf(label); at >= 0; at = message.indexOf(label, at + 1)) {
    const end = at + label.length;
    if (!word(message[at - 1]) && !word(message[end])) {
      return [message.slice(0, at), label, message.slice(end)];
    }
  }
  return null;
};

// withReceives is a destination's muted-type list after choosing whether it
// receives one type (the matrix of destinations and alert types).
export const withReceives = (muted, type, receives) => {
  const rest = (muted || []).filter((t) => t !== type);
  return receives ? rest : [...rest, type];
};

// What a notification was about, by the alert event it announced
const DELIVERY_ABOUT = {
  opened: "alert opened",
  escalated: "got worse",
  downgraded: "eased",
  resolved: "resolved",
  resumed: "still open after a pause",
};

// deliverySentence turns a delivery into "<before><name><after>" plus what it
// was about; tone is good, bad, pending or muted.
export const deliverySentence = (d) => {
  const reason = d.last_error ? `: ${d.last_error}` : "";
  const s = { name: d.destination_label, about: DELIVERY_ABOUT[d.event_kind] || d.event_kind };
  switch (d.status) {
    case "sent":
      return { tone: "good", before: "Delivered to ", ...s, after: "" };
    case "failed":
      return { tone: "bad", before: "Failed to send to ", ...s, after: reason };
    case "pending":
      return d.attempts > 0 ? { tone: "pending", before: "Retrying ", ...s, after: reason } : { tone: "pending", before: "Sending to ", ...s, after: "" };
    default:
      return { tone: "muted", before: "Not sent to ", ...s, after: reason };
  }
};

// typeFilterOptions is the type filter's choices: one audience's types, or
// all of them with a type both audiences share (delivery.failed) listed once.
export const typeFilterOptions = (types, scope) => {
  const seen = new Set();
  return (types || []).filter((t) => {
    if ((scope && t.audience_kind !== scope) || seen.has(t.key)) return false;
    seen.add(t.key);
    return true;
  });
};
